import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  People as PeopleIcon,
  MedicalServices as DentistIcon,
  EventNote as AppointmentIcon,
  AttachMoney as MoneyIcon,
  CalendarToday,
  AccessTime,
  ChevronRight,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { appointmentService } from '../../services/appointment.service';
import { patientService } from '../../services/patient.service';
import { Appointment, Patient } from '../../types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: { value: number; isPositive: boolean };
}

function StatCard({ title, value, icon, color, trend }: StatCardProps) {
  return (
    <Card elevation={0} sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={trend.isPositive ? <TrendingUp /> : <TrendingDown />}
              label={`${trend.value}%`}
              color={trend.isPositive ? 'success' : 'error'}
              sx={{ height: 24 }}
            />
          )}
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [recentPatients, setRecentPatients] = useState<Patient[]>([]);
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalDentists: 0,
    todayAppointments: 0,
    monthlyRevenue: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load today's appointments
      const today = new Date().toISOString().split('T')[0];
      const appointmentsData = await appointmentService.getAll({
        startDate: today,
        endDate: today,
      });
      setTodayAppointments(appointmentsData.slice(0, 5));

      // Load recent patients
      const patientsData = await patientService.getAll({ limit: 5 });
      setRecentPatients(Array.isArray(patientsData) ? patientsData : patientsData.data || []);

      // Mock stats - in production, these would come from API
      setStats({
        totalPatients: 248,
        totalDentists: 6,
        todayAppointments: appointmentsData.length,
        monthlyRevenue: 45680,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
      scheduled: 'primary',
      confirmed: 'success',
      completed: 'default',
      cancelled: 'error',
      no_show: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      scheduled: 'Agendada',
      confirmed: 'Confirmada',
      completed: 'Concluída',
      cancelled: 'Cancelada',
      no_show: 'Não compareceu',
    };
    return labels[status] || status;
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={1}>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Visão geral do consultório
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} lg={3}>
          {loading ? (
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Total de Pacientes"
              value={stats.totalPatients}
              icon={<PeopleIcon />}
              color="#0891b2"
              trend={{ value: 12, isPositive: true }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {loading ? (
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Dentistas Ativos"
              value={stats.totalDentists}
              icon={<DentistIcon />}
              color="#059669"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {loading ? (
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Consultas Hoje"
              value={stats.todayAppointments}
              icon={<AppointmentIcon />}
              color="#7c3aed"
              trend={{ value: 5, isPositive: true }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          {loading ? (
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          ) : (
            <StatCard
              title="Faturamento do Mês"
              value={`R$ ${stats.monthlyRevenue.toLocaleString('pt-BR')}`}
              icon={<MoneyIcon />}
              color="#ea580c"
              trend={{ value: 8, isPositive: true }}
            />
          )}
        </Grid>
      </Grid>

      {/* Lists */}
      <Grid container spacing={3}>
        {/* Today's Appointments */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="primary" />
                <Typography variant="h6" fontWeight="600">
                  Consultas de Hoje
                </Typography>
              </Box>
              <IconButton onClick={() => navigate('/calendar')}>
                <ChevronRight />
              </IconButton>
            </Box>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : todayAppointments.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Nenhuma consulta agendada para hoje
                </Typography>
              </Box>
            ) : (
              <List>
                {todayAppointments.map((appointment) => (
                  <ListItem
                    key={appointment.id}
                    secondaryAction={
                      <Chip
                        size="small"
                        label={getStatusLabel(appointment.status)}
                        color={getStatusColor(appointment.status)}
                      />
                    }
                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <AccessTime />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={appointment.patient?.name || 'Paciente'}
                      secondary={`${formatTime(appointment.startTime)} - ${appointment.dentist?.user?.name || 'Dentista'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Recent Patients */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="primary" />
                <Typography variant="h6" fontWeight="600">
                  Pacientes Recentes
                </Typography>
              </Box>
              <IconButton onClick={() => navigate('/patients')}>
                <ChevronRight />
              </IconButton>
            </Box>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : recentPatients.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Nenhum paciente cadastrado
                </Typography>
              </Box>
            ) : (
              <List>
                {recentPatients.map((patient) => (
                  <ListItem
                    key={patient.id}
                    onClick={() => navigate(`/patients/${patient.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        {patient.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={patient.name}
                      secondary={patient.phone || patient.email}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
